import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environment';
import { catchError, map, Observable, of, switchMap } from 'rxjs';
import { DataSourceConnector } from '../components/datasources/datasources.data';
import { AiFunctionCallControllerService } from '@rappider/rappider-sdk';

@Injectable({
  providedIn: 'root'
})
export class LoopbackCrudApiService {

  constructor(
    private http: HttpClient,
    private aiFunctionCallControllerService: AiFunctionCallControllerService
  ) { }

  getDefaultBaseApiUrl(): string {
    return environment.apiBaseUrl;
  }

  /**
   * Fetches a list of items from the API.
   * @param params - The parameters for the API call.
   * @returns An Observable containing the list of items.
   */
  findById(params: {
    id: string;
    url: string;
    filter?: any;
  }): Observable<any> {
    if (!params.url) {
      throw new Error('URL is required');
    }
    if (!params.id) {
      throw new Error('ID is required');
    }
    // Build the URL by replacing {{id}} with the actual id
    const url = params?.url?.replace('{{id}}', params.id);

    // If filter is provided, add it as a query param; otherwise, don't include it
    const httpParams: any = {};
    if (params.filter) {
      httpParams.filter = JSON.stringify(params.filter);
    }

    return this.http.get<any>(url, {
      params: httpParams
    });
  }

  /**
 * Fetches a list of items from the API.
 * @param params - The parameters for the API call.
 * @returns An Observable containing the list of items.
 */
  find(params: {
    url: string;
    filter?: any;
  }): Observable<any> {
    if (!params.url) {
      throw new Error('URL is required');
    }
    // If filter is provided, add it as a query param; otherwise, don't include it
    const httpParams: any = {};
    if (params.filter) {
      httpParams.filter = JSON.stringify(params.filter);
    }

    return this.http.get<any>(params.url, {
      params: httpParams
    });
  }

  updateById(params: {
    id: string;
    url: string;
    data: any;
  }): Observable<any> {
    if (!params.url) {
      throw new Error('URL is required');
    }
    if (!params.id) {
      throw new Error('ID is required');
    }
    // Build the URL by replacing {{id}} with the actual id
    const url = params?.url?.replace('{{id}}', params.id);

    // remove blacklisted fields from data
    const blacklistedFields = ['id', 'createdAt', 'updatedAt', 'createdBy', 'updatedBy', 'deletedAt', 'deletedBy', 'projectId'];
    const cleanedData = Object.keys(params.data)
      .filter(key => !blacklistedFields.includes(key))
      .reduce((obj: { [key: string]: any }, key: string) => {
        obj[key] = params.data[key];
        return obj;
      }, {} as { [key: string]: any });

    return this.http.patch<any>(url, cleanedData);
  }

  deleteById(params: {
    id: string;
    url: string;
  }): Observable<any> {
    if (!params.url) {
      throw new Error('URL is required');
    }
    if (!params.id) {
      throw new Error('ID is required');
    }
    // Build the URL by replacing {{id}} with the actual id
    const url = params?.url?.replace('{{id}}', params.id);
    return this.http.delete<any>(url);
  }

  /**
   * Creates a new item in the API.
   * @param params - The parameters for the API call.
   * @returns An Observable containing the created item.
   */
  create(params: {
    url: string;
    data: any;
  }): Observable<any> {
    if (!params.url) {
      throw new Error('URL is required');
    }
    if (!params.data) {
      throw new Error('Data is required');
    }

    return this.aiFunctionCallControllerService.create$FormData({
      body: { body: JSON.stringify(params.data) }
    });
  }

  /**
  * Counts the number of items in a collection.
  * @param params - The parameters for the API call.
  * @returns An Observable containing the count of items.
  */
  count(params: {
    url: string;
    filter?: any;
  }): Observable<any> {
    if (!params.url) {
      throw new Error('URL is required');
    }
    // If filter is provided, add it as a query param; otherwise, don't include it
    const httpParams: any = {};
    if (params.filter) {
      httpParams.filter = JSON.stringify(params.filter);
    }

    return this.http.get<any>(params.url, {
      params: httpParams
    });
  }


  ingestDataSource(params: {
    url: string;
    file: File;
    dataSourceName: string;
    dataSourceTitle: string;
    dataSourceConfig?: object;
    fileName?: string;
    fileMetadata?: any[];
  }): Observable<any> {
    if (!params.url) {
      throw new Error('URL is required');
    }
    if (!params.file) {
      throw new Error('File is required');
    }
    if (!params.dataSourceName && !params.dataSourceTitle) {
      throw new Error('Data source information is required');
    }

    const formData = new FormData();
    formData.append('file', params.file);
    formData.append('dataSource', JSON.stringify(
      {
        name: params.dataSourceName,
        title: params.dataSourceTitle,
        config: JSON.stringify(params.dataSourceConfig || {})
      }
    ));

    if (params.fileName) {
      formData.append('projectFile', JSON.stringify(
        {
          title: params.fileName || 'File',
          metadata: params.fileMetadata || []
        }
      ));
    }

    return this.http.post<any>(params.url, formData);
  }


  /**
  * Ingests a data source by sending file content as a Base64 string in a JSON payload.
  * This corresponds to the new cURL command format.
  *
  * @param params The input parameters for the data source ingestion.
  * @returns An Observable for the HTTP POST request.
  */
  ingestDataSourceAsJson(params: {
    url: string;
    file: File;
    dataSourceName: string;
    dataSourceTitle: string;
    dataSourceConfig?: object;
    fileName?: string;
    fileMetadata?: any[];
  }): Observable<any> {
    if (!params.url) {
      throw new Error('URL is required');
    }
    if (!params.file) {
      throw new Error('File is required');
    }
    if (!params.dataSourceName || !params.dataSourceTitle) {
      throw new Error('Data source information is required');
    }

    // The cURL command requires the file content to be Base64 encoded.
    // We wrap the FileReader logic in an Observable.
    return this.fileToBase64(params.file).pipe(
      switchMap(base64Content => {
        // Construct the JSON body for the request
        const body = {
          title: params.dataSourceTitle,
          name: params.dataSourceName,
          connector: DataSourceConnector.File, // Hardcoded as per the cURL command
          config: params.dataSourceConfig || {},
          file: {
            title: params.fileName || params.file.name,
            name: params.file.name,
            contentAsBase64: base64Content,
            metadata: params.fileMetadata || [],
          }
        };

        // // TODO: Replace '{AUTH_TOKEN}' with your actual token retrieval logic
        // const authToken = '{AUTH_TOKEN}';

        // // Set the required headers
        // const headers = new HttpHeaders({
        //   'Content-Type': 'application/json',
        //   'Authorization': `Bearer ${authToken}` // Or your specific auth scheme
        // });

        // Perform the HTTP POST request
        return this.http.post<any>(params.url, body);
        // return this.http.post<any>(params.url, body, { headers });
      })
    );
  }

  /**
   * Helper function to read a File object and convert it to a Base64 string.
   *
   * @param file The file to convert.
   * @returns An Observable that emits the Base64 string representation of the file.
   */
  private fileToBase64(file: File): Observable<string> {
    return new Observable(observer => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // The result includes the mime type prefix, e.g., "data:application/json;base64,"
        // The cURL example implies just the content, so we split and take the second part.
        const base64String = (reader.result as string).split(',')[1];
        observer.next(base64String);
        observer.complete();
      };
      reader.onerror = error => {
        observer.error(error);
      };
    });
  }

  /*
  curl --location '{RAPIDER_API_URL}/project-data-manager/{TABLE_NAME}/{ENTITY_ID}' \
    --header 'Authorization: {RAPIDER_AUTHORIZATION_TOKEN}'
  */
  findByIdFromProjectEntity(params: {
    entityName: string;
    entityId: string;
  }): Observable<any> {
    if (!params.entityName) {
      throw new Error('Entity name is required');
    }
    if (!params.entityId) {
      throw new Error('Entity ID is required');
    }

    const url = `${this.getDefaultBaseApiUrl()}/project-data-manager/${params.entityName}/${params.entityId}`;
    return this.http.get<any>(url);
  }

  /**
      curl --location '{RAPIDER_API_URL}/project-data-manager/{TABLE_NAME}?filter={"name": "test"}' \
      --header 'Authorization: {RAPIDER_AUTHORIZATION_TOKEN}'
   */
  findFromProjectEntity(params: {
    entityName: string;
    filter?: any;
  }): Observable<any> {
    if (!params.entityName) {
      throw new Error('Entity name is required');
    }
    // If filter is provided, add it as a query param; otherwise, don't include it
    const httpParams: any = {};
    if (params.filter) {
      httpParams.filter = JSON.stringify(params.filter);
    }

    const url = `${this.getDefaultBaseApiUrl()}/project-data-manager/${params.entityName}`;

    return this.http.get<any>(url, {
      params: httpParams
    });
  }

  /*
  curl --location '{RAPIDER_API_URL}/project-data-manager/{TABLE_NAME}' \
  --header 'Authorization: {RAPIDER_AUTHORIZATION_TOKEN}'
  --header 'Content-Type: application/json' \
  --data '{
      "name": "TEST"
  }'
  */
  createInProjectEntity(params: {
    entityName: string;
    data: any;
  }): Observable<any> {
    if (!params.entityName) {
      throw new Error('Entity name is required');
    }
    if (!params.data) {
      throw new Error('Data is required');
    }

    const url = `${this.getDefaultBaseApiUrl()}/project-data-manager/${params.entityName}`;
    return this.http.post<any>(url, params.data);
  }

  /*
  curl --location --request PATCH '{RAPIDER_API_URL}/project-data-manager/{TABLE_NAME}/{ENTITY_ID}' \
  --header 'Authorization: {RAPIDER_AUTHORIZATION_TOKEN}'
  --header 'Content-Type: application/json' \
  --data '{
      "name": "TEST5"
  }'
  */
  updateByIdInProjectEntity(params: {
    id: string;
    entityName: string;
    data: any;
  }): Observable<any> {
    if (!params.id) {
      throw new Error('ID is required');
    }
    if (!params.entityName) {
      throw new Error('Entity name is required');
    }
    if (!params.data) {
      throw new Error('Data is required');
    }

    const url = `${this.getDefaultBaseApiUrl()}/project-data-manager/${params.entityName}/${params.id}`;
    return this.http.patch<any>(url, params.data);
  }

    /*
  curl --location --request DELETE '{RAPIDER_API_URL}/project-data-manager/{TABLE_NAME}/{ENTITY_ID}' \
  --header 'Authorization: {RAPIDER_AUTHORIZATION_TOKEN}'
  --header 'Content-Type: application/json' \
  */
  deleteByIdInProjectEntity(params: {
    id: string;
    entityName: string;
  }): Observable<any> {
    if (!params.id) {
      throw new Error('ID is required');
    }
    if (!params.entityName) {
      throw new Error('Entity name is required');
    }
    const url = `${this.getDefaultBaseApiUrl()}/project-data-manager/${params.entityName}/${params.id}`;
    return this.http.delete<any>(url);
  }

  syncDataSource(params: {
    url: string;
    dataSourceId: string;
  }): Observable<any> {
    if (!params.url) {
      throw new Error('URL is required');
    }
    if (!params.dataSourceId) {
      throw new Error('Data source ID is required');
    }

    // Build the URL by replacing {{id}} with the actual dataSourceId
    const url = params?.url?.replace('{{id}}', params.dataSourceId);

    return this.http.get<any>(url, {});

  }


      /**
   * Fetches, optionally transforms, and returns data from the API.
   *
   * @param entityName The name of the data entity to fetch (e.g., 'SensorData'). This is used to construct the API endpoint.
   * @param filter An optional MongoDB-style filter object. It will be JSON-stringified and sent as a query parameter.
   * @param postDataTransformation An optional function to transform or sort the data after it's fetched.
   * This function receives the raw data from the API and should return the processed data.
   * @returns An Observable that emits the final (potentially transformed) data.
   */
  public fetchDataFromEntityApiAndTransform<T>(
    entityName: string,
    apiFilter?: any,
    postDataTransformation?: (data: T) => any
  ): Observable<any> {
    // --- 1. Construct API URL and Parameters ---
    const url = `${this.getDefaultBaseApiUrl()}/project-data-manager/${entityName}`;
    let params = new HttpParams();

    // If a filter object is provided, stringify it and add it to the HttpParams.
    // The API expects the filter as a JSON string.
    if (apiFilter && Object.keys(apiFilter).length > 0) {
      params = params.set('filter', JSON.stringify(apiFilter));
    }

    // --- 2. Fetch Data from API ---
    return this.http.get<T>(url, { params }).pipe(
      // --- 3. Use the `map` operator to transform the data ---
      map(data => {
        // Check if a transformation function was actually provided.
        if (typeof postDataTransformation === 'function') {
          // If yes, execute the function with the fetched data and return its result.
          try {
            return postDataTransformation(data);
          } catch (error) {
            console.error('An error occurred during post-data transformation:', error);
            // Depending on requirements, you might want to throw the error
            // or return the original data. Here, we'll return the original data.
            return data;
          }
        }
        // If no transformation function is provided, return the original data.
        return data;
      }),
      // --- 4. Error Handling ---
      catchError(error => {
        console.error(`API call failed for entity "${entityName}":`, error);
        // Return an empty array or another default value in case of an error.
        return of([]);
      })
    );
  }


}
